import { checkToken, checkUserType } from "../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom"; // Import useLocation to check current route path

function SpecialistProtected({ children }) {
  const dispatch = useDispatch();
  const location = useLocation(); // Hook to get current URL pathname
  const { specialist } = useSelector((state) => state.authSlice);
  const { connectStatus } = useSelector((state) => state.walletSlice); // Access the specialist slice from Redux state

  if (!checkToken(dispatch)?.valid) {
    return <Navigate to="/login" replace />;
  }

  if (checkUserType() !== "specialist") {
    return <Navigate to="/login" replace />;
  }

  // Helper to extract the actual specialist user fields.
  // In Redux authSlice, state.specialist can be:
  // 1. The user object directly (after immediate login)
  // 2. A wrapper object containing { specialist: user, verification: ... } (after fetching updated details)
  const getSpecialistUser = () => {
    if (!specialist) return null;
    if (specialist.specialist && typeof specialist.specialist === "object") {
      return specialist.specialist;
    }
    return specialist;
  };

  const specialistUser = getSpecialistUser();

  // Helper to extract verification status from the specialist slice
  const getVerification = () => {
    if (!specialist) return null;
    if (specialist.verification) {
      return specialist.verification;
    }
    return specialistUser?.verification;
  };

  const verification = getVerification();

  // Specialist must complete the verification/registration steps first
  if (verification?.status !== "verified") {
    return <Navigate to="/specialist-registration" replace />;
  }

  // ── Compulsory Stripe Connect check ──────────────────────────────────────
  //
  // IMPORTANT: connectStatus is null until the wallet page fetches it from the API.
  // We must NOT redirect when connectStatus is null (not yet loaded), because that
  // would cause a false redirect even for specialists who already have Stripe connected.
  //
  // Only redirect when:
  //   1. connectStatus has been fetched (not null), AND
  //   2. onboardingComplete is explicitly false
  //
  // This prevents the bug where a specialist with Stripe connected gets redirected
  // just because they navigate to a page before the wallet page has loaded connectStatus.
  if (specialistUser && connectStatus !== null && !connectStatus?.onboardingComplete) {
    // List of paths that are always allowed (wallet setup + Stripe callback pages)
    const isAllowedPath = [
      "/specialist/profile/wallet",
      "/specialist/profile/connect/success",
      "/specialist/profile/connect/refresh",
    ].includes(location.pathname);

    // If they are trying to access a restricted feature, redirect to the wallet page
    // with a flag to show the "Connect Stripe" popup dialog
    if (!isAllowedPath) {
      return <Navigate to="/specialist/profile/wallet?stripe_connect_required=true" replace />;
    }
  }

  return children;
}

export default SpecialistProtected;
